import type { User } from "@supabase/supabase-js";
import { runtimeUrls } from "../config/domains";

export interface BrokerSession {
  accessToken: string;
  expiresIn: number;
  csrfToken: string;
  user: User;
}

interface BrokerPayload {
  access_token: string;
  expires_in: number;
  csrf_token: string;
  user: User;
}

export class BrokerError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}

function endpoint(path: string) {
  return `${runtimeUrls().apiUrl.replace(/\/$/, "")}${path}`;
}

async function request<T>(path: string, body?: unknown, csrfToken?: string, accessToken?: string): Promise<T> {
  const response = await fetch(endpoint(path), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  });
  const payload = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) throw new BrokerError(typeof payload.detail === "string" ? payload.detail : "No pudimos completar la solicitud.", response.status);
  return payload as T;
}

function normalizeSession(payload: BrokerPayload): BrokerSession {
  return { accessToken: payload.access_token, expiresIn: payload.expires_in, csrfToken: payload.csrf_token, user: payload.user };
}

export const sessionBroker = {
  session: async () => normalizeSession(await request<BrokerPayload>("/api/auth/session")),
  signIn: async (email: string, password: string) => normalizeSession(await request<BrokerPayload>("/api/auth/login", { email, password })),
  verify: async (tokenHash: string, type: string) => normalizeSession(await request<BrokerPayload>("/api/auth/verify", { token_hash: tokenHash, type })),
  recover: (email: string) => request<{ status: string }>("/api/auth/recover", { email }),
  updatePassword: (password: string, csrfToken: string) => request<{ status: string }>("/api/auth/password", { password }, csrfToken),
  signOut: (csrfToken: string) => request<{ status: string }>("/api/auth/logout", {}, csrfToken),
  mfaStatus: (accessToken: string) => request<{ aal: "aal1" | "aal2"; factors: Array<{ id: string; status: string; friendly_name?: string }> }>("/api/auth/mfa/status", {}, undefined, accessToken),
  enrollMfa: (accessToken: string, csrfToken: string) => request<{ factor_id: string; qr_code: string }>("/api/auth/mfa/enroll", {}, csrfToken, accessToken),
  challengeMfa: (factorId: string, accessToken: string, csrfToken: string) => request<{ id: string; expires_at?: number }>("/api/auth/mfa/challenge", { factor_id: factorId }, csrfToken, accessToken),
  verifyMfa: async (factorId: string, challengeId: string, code: string, accessToken: string, csrfToken: string) => normalizeSession(await request<BrokerPayload>("/api/auth/mfa/verify", { factor_id: factorId, challenge_id: challengeId, code }, csrfToken, accessToken)),
};
